<script setup>
import { computed, ref, watch } from "vue";
import ImageUpload from "@/components/ImageUpload";
import InputField from "@/components/forms/InputField.vue";
import { HTTP_PREFIX_PLACEHOLDER } from "@/env.js";

const props = defineProps({
    defaultData: {
        name: {
            type: String,
            default: "",
        },
        website: {
            type: String,
            default: "",
        },
        description: {
            type: String,
            default: "",
        },
        logo: {
            type: String,
            default: "",
        },
        cover: {
            type: String,
            default: "",
        },
        phone: {
            type: String,
            default: "",
        },
        email: {
            type: String,
            default: "",
        },
    },
    errors: {
        name: {
            type: String,
            default: "",
        },
        description: {
            type: String,
            default: "",
        },
        email: {
            type: String,
            default: "",
        },
        logo: {
            type: String,
            default: "",
        },
        cover: {
            type: String,
            default: "",
        },
    },
});

const organisation = ref({
    name: "",
    website: "",
    description: "",
    firstname: "",
    lastname: "",
    logo: "",
    cover: "",
    phone: "",
    email: "",
});

const emit = defineEmits(["updated"]);

function setImg(img, type) {
    organisation.value[type] = img;
}

// eslint-disable-next-line no-unsafe-optional-chaining
const descriptionCharLeft = computed(() => 3000 - organisation.value?.description.length);

watch(organisation, () => {
    emit("updated", organisation.value);
});

watch(props, () => {
    organisation.value = props.defaultData;

    if (organisation.value.website == null || organisation.value?.website.trim() === "") {
        organisation.value.website = HTTP_PREFIX_PLACEHOLDER;
    }
});

function removeProfileImg() {
    organisation.value.logo = "";
}

function removeCoverImg() {
    organisation.value.cover = "";
}
</script>

<template>
    <div class="d-flex flex-row">
        <div class="mt-4 col col-4 mx-auto">
            <div>
                <ImageUpload
                    class=""
                    @cropped="setImg($event, 'logo')"
                    @removed="removeProfileImg"
                    :main-img="organisation.logo"
                    :rounded="true"
                    :show-remove-icon="true"
                    :show-required="false"
                    block-title="Profile picture"
                    modal-title="Profile picture"
                    :aspect-ratio="1/1"
                />

                <div v-if="errors.logo">
                    <div class="error-text" data-test="validation-error">{{ errors.logo }}</div>
                </div>

                <ImageUpload
                    class="mt-4"
                    @cropped="setImg($event, 'cover')"
                    @removed="removeCoverImg"
                    :main-img="organisation.cover"
                    :show-remove-icon="true"
                    :show-required="false"
                    block-title="Cover picture"
                    modal-title="Cover picture"
                    :aspect-ratio="16/7"
                />

                <div v-if="errors.cover">
                    <div class="error-text" data-test="validation-error">{{ errors.cover }}</div>
                </div>

                <div class="d-flex flex-row justify-content-between mt-3">
                    <div class="d-flex flex-column personal-info-block first-name-input">
                        <InputField
                            label-text="First name"
                            placeholder="First name"
                            v-model="organisation.firstname"
                            :required="true"
                            :value="organisation.firstname"
                            :error="errors.firstname"
                        />
                    </div>
                    <div class="d-flex flex-column personal-info-block last-name-input">
                        <InputField
                            label-text="Last name"
                            placeholder="Last name"
                            v-model="organisation.lastname"
                            :required="true"
                            :value="organisation.lastname"
                            :error="errors.lastname"
                        />
                    </div>
                </div>

                <div class="d-flex flex-column">
                    <div class="d-flex flex-column mt-3">

                        <InputField
                            label-text="Organisation name"
                            placeholder="Enter an organisation name"
                            v-model="organisation.name"
                            :maxlength="65"
                            :value="organisation.name"
                            :required="true"
                            :error="errors.name"
                        />
                    </div>

                    <div class="d-flex flex-column">
                        <label class="show organiser-description mt-3">
                            Organiser description<span class="required-field">*</span>
                        </label>
                        <textarea
                            v-model="organisation.description"
                            maxlength="3000"
                            :class="{'error-input': errors.description }"
                            placeholder="Enter a description"
                            rows="3"
                        ></textarea>
                        <div class="information-text">{{ descriptionCharLeft }} characters left</div>
                        <div v-if="errors.description">
                            <div class="error-text" data-test="validation-error">{{ errors.description }}</div>
                        </div>
                    </div>

                    <div class="d-flex flex-column mt-3">
                        <InputField
                            label-text="Email address"
                            placeholder="Enter an email"
                            v-model="organisation.email"
                            :value="organisation.email"
                            :required="true"
                            :error="errors.email"
                            :maxlength="255"
                        />
                    </div>

                    <div class="d-flex flex-column mt-3">
                        <InputField
                            label-text="Phone number (optional)"
                            placeholder="Enter a number"
                            v-model="organisation.phone"
                            :value="organisation.phone"
                        />
                    </div>

                    <div class="d-flex flex-column mt-3">
                        <InputField
                            label-text="Website (optional)"
                            placeholder="Enter a url"
                            v-model="organisation.website"
                            :value="organisation.website"
                        />
                    </div>

                    <div>
                        <div class="error-text" data-test="validation-error">{{ errors.website }}</div>
                    </div>
                </div>
            </div>

        </div>

    </div>
</template>

<style scoped>
.organiser-description {
    font-size: 0.75rem;
}

.personal-info-block {
    /*max-width: 270px;*/
}

textarea {
    background: #FFFFFF;
    border: 1px solid #DDDDDD;
    box-sizing: border-box;
    border-radius: 10px;
    padding: 12px;
    outline: none;
    resize: none;
}

label {
    font-weight: 600;
    visibility: visible !important;
    opacity: 1 !important;
}

.error-input {
    border: 1px solid #ed5a5e;
}

.first-name-input,
.last-name-input {
    width: 47%;
}

.last-name-input {
    margin-left: auto;
}

.information-text {
    font-style: italic;
    font-weight: normal;
    font-size: 14px;
    line-height: 22px;
    color: #636066;
}
</style>
